import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant, useAuthUser } from 'store/auth';
import { Spinner } from 'Components/common';
import { Dialog, TextField, PageHeader, Button } from 'Components/ui';
import { Main as Container } from 'Components/Main';
import { TrustScoreDetail } from './TrustScoreDetail';
import { GapAnalysisView } from './GapAnalysisView';
import { ExecutiveApprovalModal } from './ExecutiveApprovalModal';
import { AssessmentTimeline } from './AssessmentTimeline';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import { ASSESSMENT_STATUS_I18N_KEY } from 'utils/assessment-status';
import {
  getAssessmentById,
  getTrustScore,
  getControlResponses,
  sendInvitation,
  resendInvitation,
  executiveApproveAssessment,
  executiveRejectAssessment,
  getExecutiveApprovalHistory,
  getMyPendingExecutiveApprovals,
  getAssessmentTimeline,
  getVendorLink,
} from 'services/vendor-assessment-service';
import {
  PageWrap, FrameworkTag,
  NavActions,
  ResendBtn, ReviewBtn, ExecRequestBtn,
  DraftBanner, PendingBanner, BannerInner, BannerText,
  MainGrid, FullWidth,
  DetailCard, CardTitle, FieldsGrid, FieldLabel, FieldValueBold, FieldValue, StatusBadge,
  TrustCardInner, ScoreValue, ScoreClass, NoScoreText, ViewDetailsBtn, TrustLoadingBox,
  ExecCardTitle, ExecCardSubtitle, ExecWarningBox, ExecWarningText,
  JustificationLabel, JustificationHelper,
  ApprovalActionsRow, RejectBtn, ApproveBtn,
  LoadingContainer, ErrorWrap, ErrorBox, ErrorText, ErrorBackBtn,
} from './AssessmentDetail.styles';

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

const STATUS_STYLE = {
  'Draft':                      { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
  'Pending Vendor Response':    { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'   },
  'Submitted':                  { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)'  },
  'Completed':                  { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)'  },
  'Under Review':               { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)'  },
  'Pending Executive Approval': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'   },
  'Approved':                   { color: 'var(--vo-success)', bg: 'var(--vo-success-10)'  },
  'Rejected':                   { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)'   },
  'Contractual Review':         { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
};

const STATUS_I18N_KEY = ASSESSMENT_STATUS_I18N_KEY;

const getScoreColor = (pct) =>
  pct >= 90 ? 'var(--vo-success)' : pct >= 70 ? 'var(--vo-warning)' : 'var(--vo-danger)';

const ArrowLeftSVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const InfoCircleSVG = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const TriangleSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ShareSVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const EmailSVG = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export const AssessmentDetail = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showTrustScore, setShowTrustScore] = useState(false);
  const [showGapAnalysis, setShowGapAnalysis] = useState(false);
  const [showExecutiveApproval, setShowExecutiveApproval] = useState(false);
  const [executiveJustification, setExecutiveJustification] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const tenant = useSelector(useAuthTenant);
  const authUser = useSelector(useAuthUser);

  const { data: assessmentData, isLoading: assessmentLoading, error: assessmentError } = useQuery(
    ['assessment', id],
    () => getAssessmentById(id),
    { enabled: !!id, retry: false }
  );

  const { data: trustScoreData, isLoading: trustScoreLoading } = useQuery(
    ['trust-score', id],
    () => getTrustScore(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
        ['Under Review', 'Pending Executive Approval', 'Approved', 'Rejected'].includes(assessmentData?.data?.data?.status),
      retry: false,
    }
  );

  const { data: controlResponsesData } = useQuery(
    ['control-responses', id],
    () => getControlResponses(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
        ['Under Review', 'Pending Executive Approval', 'Approved', 'Rejected'].includes(assessmentData?.data?.data?.status),
      retry: false,
    }
  );

  const { data: approvalHistoryData } = useQuery(
    ['executive-approval-history', id],
    () => getExecutiveApprovalHistory(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
        assessmentData?.data?.data?.status === 'Pending Executive Approval',
      retry: false,
    }
  );

  // The caller's own pending approvals — the same lookup the approve/reject endpoints authorize
  // against, so the decision panel shows only for the executive the request was sent to.
  const { data: myPendingApprovalsData, isLoading: myPendingApprovalsLoading } = useQuery(
    ['executive-approvals-pending', authUser?.userId, tenant?.slug],
    () => getMyPendingExecutiveApprovals(tenant?.slug),
    {
      enabled: !!id && !!tenant?.slug && assessmentData?.data?.success &&
        assessmentData?.data?.data?.status === 'Pending Executive Approval',
      retry: false,
    }
  );

  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError,
  } = useQuery(
    ['assessment-timeline', id],
    () => getAssessmentTimeline(id),
    { enabled: !!id && assessmentData?.data?.success, retry: false }
  );

  // Fetched only when the share dialog opens (each retrieval is audit-logged server-side).
  const {
    data: vendorLinkData,
    isLoading: vendorLinkLoading,
    error: vendorLinkError,
  } = useQuery(
    ['vendor-link', id],
    () => getVendorLink(id, tenant?.slug),
    { enabled: showShare && !!id && !!tenant?.slug, retry: false, cacheTime: 0, staleTime: 0 }
  );
  const vendorLink = vendorLinkData?.data?.data || null;

  const copyVendorLink = async () => {
    if (!vendorLink?.link) return;
    try {
      await navigator.clipboard.writeText(vendorLink.link);
    } catch {
      // Clipboard API unavailable (insecure context): fall back to selecting the field text.
      const field = document.getElementById('vendor-share-link');
      field?.select();
      document.execCommand?.('copy');
    }
    setLinkCopied(true);
  };

  const closeShare = () => {
    setShowShare(false);
    setLinkCopied(false);
  };

  const assessment      = assessmentData?.data?.success ? assessmentData.data.data : null;
  const trustScore      = trustScoreData?.data?.success ? trustScoreData.data.data : null;
  const controlResponses = controlResponsesData?.data?.success ? controlResponsesData.data.data : [];
  const approvalHistory = approvalHistoryData?.data?.success ? approvalHistoryData.data.data : null;
  const myPendingApprovals = myPendingApprovalsData?.data?.success ? myPendingApprovalsData.data.data : [];
  const isDesignatedExecutive = !myPendingApprovalsLoading &&
    myPendingApprovals.some((a) => String(a.assessmentId) === String(id));
  const timelineEntries = timelineData?.data?.success ? timelineData.data.data.timeline : [];
  const timelineIsError = timelineError || (timelineData && !timelineData?.data?.success);

  const sendMutation = useMutation(
    () => sendInvitation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        Dialog.fire({ icon: 'success', title: t('gap-analysis:assessment.detail.swal.invitationSentTitle'), text: t('gap-analysis:assessment.detail.swal.invitationSentText'), timer: 3000 });
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.detail.swal.errorTitle'), message: error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorSend') });
      },
    }
  );

  const resendMutation = useMutation(
    () => resendInvitation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        Dialog.fire({ icon: 'success', title: t('gap-analysis:assessment.detail.swal.invitationResentTitle'), text: t('gap-analysis:assessment.detail.swal.invitationResentText'), timer: 2000 });
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.detail.swal.errorTitle'), message: error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorResend') });
      },
    }
  );

  const approveMutation = useMutation(
    async () => {
      const response = await executiveApproveAssessment(id, executiveJustification);
      if (!response.data?.success) throw new Error(response.data?.error || 'Failed to approve assessment');
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        queryClient.invalidateQueries(['executive-approval-history', id]);
        setExecutiveJustification('');
        Dialog.fire({ icon: 'success', title: t('gap-analysis:assessment.detail.swal.approvedTitle'), text: t('gap-analysis:assessment.detail.swal.approvedText'), timer: 3000 });
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.detail.swal.errorTitle'), message: error.message || error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorApprove') });
      },
    }
  );

  const rejectMutation = useMutation(
    async () => {
      const response = await executiveRejectAssessment(id, executiveJustification);
      if (!response.data?.success) throw new Error(response.data?.error || t('gap-analysis:assessment.detail.swal.errorReject'));
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        queryClient.invalidateQueries(['executive-approval-history', id]);
        setExecutiveJustification('');
        Dialog.fire({ icon: 'success', title: t('gap-analysis:assessment.detail.swal.rejectedTitle'), text: t('gap-analysis:assessment.detail.swal.rejectedText'), timer: 3000 });
      },
      onError: (error) => {
        Dialog.alert({ icon: 'error', title: t('gap-analysis:assessment.detail.swal.errorTitle'), message: error.message || error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorReject') });
      },
    }
  );

  if (assessmentLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (assessmentError || !assessment) {
    return (
      <Container>
        <ErrorWrap>
          <ErrorBox>
            <ErrorText>{t('gap-analysis:assessment.detail.notFound')}</ErrorText>
          </ErrorBox>
          <ErrorBackBtn onClick={() => navigate('/vendor-assessments')}>
            <ArrowLeftSVG />
            {t('gap-analysis:assessment.detail.backButton')}
          </ErrorBackBtn>
        </ErrorWrap>
      </Container>
    );
  }

  const statusStyle   = STATUS_STYLE[assessment.status] || STATUS_STYLE['Draft'];
  const statusI18nKey = STATUS_I18N_KEY[assessment.status] || 'draft';
  const scoreColor    = trustScore ? getScoreColor(trustScore.percentage) : null;

  const isExecApproval  = assessment.status === 'Pending Executive Approval';
  const isPending       = assessment.status === 'Pending Vendor Response';
  const isDraft         = assessment.status === 'Draft';
  const isUnderReview   = assessment.status === 'Under Review';

  return (
    <Container>
      <PageWrap>
        {/* ── Page header ── */}
        <PageHeader
          title={`${t('gap-analysis:assessment.detail.title')} — ${str(assessment.vendorName)}`}
          segments={[
            { label: t('common:breadcrumbs.thirdPartyRiskManagement') },
            { label: t('gap-analysis:assessment.page.title'), route: '/vendor-assessments' },
            { label: str(assessment.vendorName) },
          ]}
          actions={
            <NavActions>
              {isDraft && (
                <Button
                  variant="primary"
                  onClick={() => sendMutation.mutate()}
                  disabled={sendMutation.isLoading}
                  isLoading={sendMutation.isLoading}
                  startIcon={<EmailSVG />}
                  sx={{ height: '40px', padding: '0 18px' }}
                >
                  {sendMutation.isLoading ? t('gap-analysis:assessment.detail.sending') : t('gap-analysis:assessment.detail.sendInvitation')}
                </Button>
              )}
              {isPending && (
                <ResendBtn onClick={() => setShowShare(true)} data-testid="share-with-vendor">
                  <ShareSVG />
                  {t('gap-analysis:assessment.detail.share.button')}
                </ResendBtn>
              )}
              {isUnderReview && (
                <>
                  <ReviewBtn onClick={() => setShowGapAnalysis(true)}>
                    {t('gap-analysis:assessment.detail.actions.reviewAndUnlock')}
                  </ReviewBtn>
                  {trustScore && trustScore.percentage < 70 && (
                    <ExecRequestBtn onClick={() => setShowExecutiveApproval(true)}>
                      {t('gap-analysis:assessment.detail.actions.requestExecutiveApproval')}
                    </ExecRequestBtn>
                  )}
                </>
              )}
            </NavActions>
          }
        />
        <FrameworkTag>{str(assessment.frameworkName)}</FrameworkTag>

        {/* ── Status banners ── */}
        {isDraft && (
          <DraftBanner>
            <BannerInner>
              <InfoCircleSVG color="var(--color-brand)" />
              <BannerText dangerouslySetInnerHTML={{ __html: t('gap-analysis:assessment.detail.alerts.draft') }} />
            </BannerInner>
          </DraftBanner>
        )}
        {isPending && (
          <PendingBanner>
            <BannerInner>
              <InfoCircleSVG color="var(--vo-warning)" />
              <BannerText $warning dangerouslySetInnerHTML={{ __html: t('gap-analysis:assessment.detail.alerts.pendingVendorResponse', { date: formatDateTimeToDateTimeBR(assessment.tokenExpiration) }) }} />
            </BannerInner>
          </PendingBanner>
        )}
        {isExecApproval && (
          <PendingBanner>
            <BannerInner>
              <TriangleSVG />
              <BannerText $warning dangerouslySetInnerHTML={{ __html: t('gap-analysis:assessment.detail.alerts.execApproval', { date: approvalHistory?.requestedAt ? formatDateTimeToDateTimeBR(approvalHistory.requestedAt) : '...' }) }} />
            </BannerInner>
          </PendingBanner>
        )}

        {/* ── Main grid ── */}
        <MainGrid>
          {/* Assessment info card */}
          <DetailCard>
            <CardTitle>{t('gap-analysis:assessment.detail.sections.assessmentInfo')}</CardTitle>
            <FieldsGrid>
              <div>
                <FieldLabel>{t('gap-analysis:assessment.detail.fields.vendor')}</FieldLabel>
                <FieldValueBold>{str(assessment.vendorName)}</FieldValueBold>
              </div>
              <div>
                <FieldLabel>{t('gap-analysis:assessment.detail.fields.framework')}</FieldLabel>
                <FieldValue>{str(assessment.frameworkName)}</FieldValue>
              </div>
              <div>
                <FieldLabel>{t('common:labels.status')}</FieldLabel>
                <StatusBadge $color={statusStyle.color} $bg={statusStyle.bg}>
                  {t(`gap-analysis:assessment.list.statuses.${statusI18nKey}`)}
                </StatusBadge>
              </div>
              <div>
                <FieldLabel>{t('gap-analysis:assessment.detail.fields.targetMaturity')}</FieldLabel>
                <FieldValue>{t('gap-analysis:assessment.detail.fields.level', { level: assessment.targetMaturityLevel })}</FieldValue>
              </div>
              <div>
                <FieldLabel>{t('gap-analysis:assessment.detail.fields.createdDate')}</FieldLabel>
                <FieldValue>{formatDateTimeToDateTimeBR(assessment.createdAt)}</FieldValue>
              </div>
              {assessment.submittedAt && (
                <div>
                  <FieldLabel>{t('gap-analysis:assessment.detail.fields.submittedDate')}</FieldLabel>
                  <FieldValue>{formatDateTimeToDateTimeBR(assessment.submittedAt)}</FieldValue>
                </div>
              )}
              {assessment.emailSentAt && (
                <div>
                  <FieldLabel>{t('gap-analysis:assessment.detail.fields.emailSent')}</FieldLabel>
                  <FieldValue>{formatDateTimeToDateTimeBR(assessment.emailSentAt)}</FieldValue>
                </div>
              )}
              {assessment.tokenExpiration && (
                <div>
                  <FieldLabel>{t('gap-analysis:assessment.detail.fields.accessExpires')}</FieldLabel>
                  <FieldValue>{formatDateTimeToDateTimeBR(assessment.tokenExpiration)}</FieldValue>
                </div>
              )}
            </FieldsGrid>
          </DetailCard>

          {/* Trust Score card */}
          <DetailCard style={{ display: 'flex', flexDirection: 'column' }}>
            <CardTitle>{t('gap-analysis:assessment.detail.sections.trustScore')}</CardTitle>
            {trustScoreLoading ? (
              <TrustLoadingBox><Spinner $size={30} /></TrustLoadingBox>
            ) : trustScore ? (
              <TrustCardInner>
                <ScoreValue $color={scoreColor}>{trustScore.percentage}{'%'}</ScoreValue>
                <ScoreClass $color={scoreColor}>{str(trustScore.classification)}</ScoreClass>
                <ViewDetailsBtn onClick={() => setShowTrustScore(true)}>
                  {t('gap-analysis:assessment.detail.trustScore.viewDetails')}
                </ViewDetailsBtn>
              </TrustCardInner>
            ) : (
              <TrustCardInner>
                {/* Tier 3 has no questionnaire (spec FR-009), so there is nothing for the vendor to complete. */}
                <NoScoreText data-testid="trust-score-empty">
                  {t(assessment.status === 'Contractual Review'
                    ? 'gap-analysis:assessment.detail.trustScore.notApplicableContractual'
                    : 'gap-analysis:assessment.detail.trustScore.notCalculated')}
                </NoScoreText>
              </TrustCardInner>
            )}
          </DetailCard>

          {/* Executive Approval section — BEFORE Timeline */}
          {isExecApproval && isDesignatedExecutive && (
            <FullWidth>
              <DetailCard>
                <ExecCardTitle>{t('gap-analysis:assessment.detail.sections.executiveApprovalRequired')}</ExecCardTitle>
                <ExecCardSubtitle>{t('gap-analysis:assessment.detail.execSubtitle')}</ExecCardSubtitle>

                {approvalHistory && (
                  <ExecWarningBox>
                    <ExecWarningText>
                      {t('gap-analysis:assessment.detail.approvalRequested')}{' '}
                      <strong>{formatDateTimeToDateTimeBR(approvalHistory.requestedAt)}</strong>
                      {approvalHistory.notes && <> {'—'} {str(approvalHistory.notes)}</>}
                    </ExecWarningText>
                  </ExecWarningBox>
                )}

                <JustificationLabel htmlFor="exec-justification">
                  {t('gap-analysis:assessment.detail.justification')} *
                </JustificationLabel>
                <TextField
                  id="exec-justification"
                  required
                  multiline
                  fullWidth
                  value={executiveJustification}
                  onChange={(e) => setExecutiveJustification(e.target.value)}
                  placeholder={t('gap-analysis:assessment.detail.justificationPlaceholder')}
                  rows={3}
                />
                <JustificationHelper>{t('gap-analysis:assessment.detail.justificationHelper')}</JustificationHelper>

                <ApprovalActionsRow>
                  <RejectBtn
                    onClick={() => rejectMutation.mutate()}
                    disabled={!executiveJustification.trim() || rejectMutation.isLoading || approveMutation.isLoading}
                  >
                    {rejectMutation.isLoading ? <Spinner $size={18} /> : t('gap-analysis:assessment.detail.reject')}
                  </RejectBtn>
                  <ApproveBtn
                    onClick={() => approveMutation.mutate()}
                    disabled={!executiveJustification.trim() || approveMutation.isLoading || rejectMutation.isLoading}
                  >
                    {approveMutation.isLoading ? <Spinner $size={18} /> : t('gap-analysis:assessment.detail.approve')}
                  </ApproveBtn>
                </ApprovalActionsRow>
              </DetailCard>
            </FullWidth>
          )}

          {/* Timeline — always last */}
          <FullWidth>
            <DetailCard>
              <CardTitle>{t('gap-analysis:assessment.detail.sections.activityTimeline')}</CardTitle>
              <AssessmentTimeline
                entries={timelineEntries}
                isLoading={timelineLoading}
                isError={timelineIsError}
              />
            </DetailCard>
          </FullWidth>
        </MainGrid>

        <Dialog.custom open={showTrustScore} onClose={() => setShowTrustScore(false)} title={t('gap-analysis:assessment.detail.dialogs.trustScoreDetails')}>
          {trustScore && <TrustScoreDetail trustScore={trustScore} />}
        </Dialog.custom>

        <Dialog.custom open={showGapAnalysis} onClose={() => setShowGapAnalysis(false)} title={t('gap-analysis:assessment.detail.dialogs.gapAnalysis')} size="xl">
          <GapAnalysisView
            assessment={assessment}
            controls={controlResponses}
            trustScore={trustScore}
            onRefresh={() => {
              queryClient.invalidateQueries(['assessment', id]);
              queryClient.invalidateQueries(['control-responses', id]);
              queryClient.invalidateQueries(['trust-score', id]);
            }}
          />
        </Dialog.custom>

        <Dialog.custom
          open={showShare}
          onClose={closeShare}
          title={t('gap-analysis:assessment.detail.share.title')}
          actions={
            <>
              <Button variant="outline" onClick={closeShare} sx={{ height: '40px', padding: '0 18px' }}>
                {t('common:buttons.close')}
              </Button>
              <Button
                variant="outline"
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isLoading}
                startIcon={<EmailSVG />}
                sx={{ height: '40px', padding: '0 18px' }}
              >
                {resendMutation.isLoading ? t('gap-analysis:assessment.detail.sending') : t('gap-analysis:assessment.detail.share.resendEmail')}
              </Button>
              <Button
                variant="primary"
                onClick={copyVendorLink}
                disabled={!vendorLink?.link}
                sx={{ height: '40px', padding: '0 18px' }}
                data-testid="copy-vendor-link"
              >
                {linkCopied ? t('gap-analysis:assessment.detail.share.copied') : t('gap-analysis:assessment.detail.share.copyLink')}
              </Button>
            </>
          }
        >
          <FieldLabel style={{ marginBottom: 12, textTransform: 'none', letterSpacing: 0, fontSize: 13 }}>
            {t('gap-analysis:assessment.detail.share.description', { vendor: str(assessment.vendorName) })}
          </FieldLabel>
          {vendorLinkLoading && <Spinner />}
          {vendorLinkError && (
            <ErrorText data-testid="vendor-link-error">
              {vendorLinkError.code === 'VENDOR_LINK_NOT_ACTIVE'
                ? t('gap-analysis:assessment.detail.share.notActive')
                : t('gap-analysis:assessment.detail.share.loadError')}
            </ErrorText>
          )}
          {vendorLink?.link && (
            <>
              <TextField
                id="vendor-share-link"
                fullWidth
                value={vendorLink.link}
                inputProps={{ readOnly: true, 'data-testid': 'vendor-link-field' }}
                onFocus={(e) => e.target.select()}
              />
              {vendorLink.expiresAt && (
                <FieldValue style={{ marginTop: 8, fontSize: 12 }}>
                  {t('gap-analysis:assessment.detail.share.expires', { date: formatDateTimeToDateTimeBR(vendorLink.expiresAt) })}
                </FieldValue>
              )}
            </>
          )}
        </Dialog.custom>

        <Dialog.custom open={showExecutiveApproval} onClose={() => setShowExecutiveApproval(false)} title={t('gap-analysis:assessment.detail.dialogs.executiveApproval')}>
          <ExecutiveApprovalModal
            assessmentId={assessment._id}
            trustScore={trustScore}
            onClose={() => setShowExecutiveApproval(false)}
            onSuccess={() => {
              setShowExecutiveApproval(false);
              queryClient.invalidateQueries(['assessment', id]);
            }}
          />
        </Dialog.custom>
      </PageWrap>
    </Container>
  );
};
